import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PersonaInterviewTab } from '@/components/research/PersonaInterviewTab';
import { MethodologyWorkspace } from '@/components/methodologies/MethodologyWorkspace';
import { METHODOLOGIES_ENABLED } from '@/utils/config';
import { cn } from '@/lib/utils';

const RESEARCH_TABS = ['interview', 'custdev-questions', 'survey', 'research-analysis'] as const;
type ResearchTab = (typeof RESEARCH_TABS)[number];
const DEFAULT_TAB: ResearchTab = 'interview';

function isResearchTab(value: string | null): value is ResearchTab {
  return value !== null && (RESEARCH_TABS as readonly string[]).includes(value);
}

/**
 * Route shell for `/research`: four research tools behind one tab bar, the
 * active tab addressable via `?tab=` (unknown/missing value → the first
 * tab). Tabs 2-4 are the F-02 methodology engine mounted with a fixed
 * `methodId` — one instance per tab, not one instance whose prop changes, but
 * `key={methodId}` is still set explicitly: it's the same guarantee the
 * `MethodologyWorkspace` internal reset relies on (see its `methodId` effect)
 * so a result can never surface under the wrong tab regardless of how this
 * shell evolves. Tab 1 is the pre-existing persona chat, moved unchanged into
 * `PersonaInterviewTab` — `ResearchPage.test.tsx` keeps covering it as-is.
 *
 * The whole tab bar sits behind `METHODOLOGIES_ENABLED`. The backend route is
 * mounted unconditionally, so without this gate a merge into `main` would
 * publish the research tools to paying users the moment the branch shipped;
 * with the flag off the page renders exactly what it rendered before F-03.
 */
export default function ResearchPage() {
  const { t } = useTranslation('research');
  const { t: tMethod } = useTranslation('methodologies');
  const [searchParams, setSearchParams] = useSearchParams();

  // The persona chat goes full-bleed once a persona is picked (its own
  // sticky header, page padding collapsed via setNoPadding) — the tab bar
  // and a page header would just sit awkwardly above that layout.
  const [isImmersive, setIsImmersive] = useState(false);
  const handleImmersiveChange = useCallback((value: boolean) => setIsImmersive(value), []);

  // Flag off → today's page, no tab bar and no methodology tools.
  if (!METHODOLOGIES_ENABLED) {
    return <PersonaInterviewTab onImmersiveChange={handleImmersiveChange} />;
  }

  const requestedTab = searchParams.get('tab');
  const activeTab: ResearchTab = isResearchTab(requestedTab) ? requestedTab : DEFAULT_TAB;
  // Set by links from the mentor chat to open one specific result.
  const recordId = searchParams.get('recordId') ?? undefined;

  const handleTabChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', value);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange}>
      {!isImmersive && (
        <>
          {/* Tabs stay on top for every tool: the persona interview renders its own
              header below them, so the methodology tabs follow the same order. */}
          <TabsList className="mb-[24px]">
            <TabsTrigger value="interview">{t('tabs.interview')}</TabsTrigger>
            <TabsTrigger value="custdev-questions">{t('tabs.custdevQuestions')}</TabsTrigger>
            <TabsTrigger value="survey">{t('tabs.survey')}</TabsTrigger>
            <TabsTrigger value="research-analysis">{t('tabs.analysis')}</TabsTrigger>
          </TabsList>
          {activeTab !== 'interview' && (
            <PageHeader
              title={tMethod(`methods.${activeTab}.title`)}
              description={tMethod(`methods.${activeTab}.why`)}
            />
          )}
        </>
      )}

      <TabsContent value="interview" className={cn(!isImmersive && 'mt-[8px]')}>
        <PersonaInterviewTab onImmersiveChange={handleImmersiveChange} />
      </TabsContent>
      <TabsContent value="custdev-questions">
        <MethodologyWorkspace key="custdev-questions" methodId="custdev-questions" initialRecordId={recordId} />
      </TabsContent>
      <TabsContent value="survey">
        <MethodologyWorkspace key="survey" methodId="survey" initialRecordId={recordId} />
      </TabsContent>
      <TabsContent value="research-analysis">
        <MethodologyWorkspace key="research-analysis" methodId="research-analysis" initialRecordId={recordId} />
      </TabsContent>
    </Tabs>
  );
}
